import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, ApiError, Service, CreateServiceInput, UpdateServiceInput } from "../lib/api";

const SERVICE_COLORS = [
  { value: "#ef4444", label: "Red" },
  { value: "#f97316", label: "Orange" },
  { value: "#f59e0b", label: "Amber" },
  { value: "#84cc16", label: "Lime" },
  { value: "#22c55e", label: "Green" },
  { value: "#14b8a6", label: "Teal" },
  { value: "#3b82f6", label: "Blue" },
  { value: "#8b5cf6", label: "Violet" },
  { value: "#ec4899", label: "Pink" },
  { value: "#6b7280", label: "Gray" },
];

interface ServiceFormProps {
  service?: Service;
  onClose: () => void;
  onSuccess: () => void;
}

function ServiceForm({ service, onClose, onSuccess }: ServiceFormProps) {
  const isEdit = !!service;
  const [name, setName] = useState(service?.name ?? "");
  const [description, setDescription] = useState(service?.description ?? "");
  const [duration, setDuration] = useState(String(service?.duration ?? 60));
  const [price, setPrice] = useState(service?.price ?? "0.00");
  const [color, setColor] = useState(service?.color ?? "#3b82f6");
  const [category, setCategory] = useState(service?.category ?? "");
  const [requiresDog, setRequiresDog] = useState(service?.requiresDog ?? true);
  const [maxDogs, setMaxDogs] = useState(String(service?.maxDogsPerAppointment ?? 1));
  // New program/class fields
  const [isProgram, setIsProgram] = useState(service?.isProgram ?? false);
  const [isGroupClass, setIsGroupClass] = useState(service?.isGroupClass ?? false);
  const [maxParticipants, setMaxParticipants] = useState(String(service?.maxParticipants ?? 10));
  const [bufferBefore, setBufferBefore] = useState(String(service?.bufferBeforeMinutes ?? ""));
  const [bufferAfter, setBufferAfter] = useState(String(service?.bufferAfterMinutes ?? ""));
  const [depositAmount, setDepositAmount] = useState(service?.depositAmount ?? "");
  const [depositType, setDepositType] = useState<"fixed" | "percentage" | "">(
    service?.depositType ?? ""
  );
  const [isPublic, setIsPublic] = useState(service?.isPublic ?? true);
  const [error, setError] = useState("");
  const [selectedCalendarIds, setSelectedCalendarIds] = useState<string[]>(service?.calendarIds ?? []);

  // Fetch calendars for the multi-select
  const { data: calendarsData } = useQuery({
    queryKey: ["calendars"],
    queryFn: () => api.calendars.list({ limit: 100 }),
  });
  const calendars = calendarsData?.data ?? [];

  const createMutation = useMutation({
    mutationFn: (data: CreateServiceInput) => api.services.create(data),
    onSuccess: () => {
      onSuccess();
      onClose();
    },
    onError: (err) => {
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError("Failed to create service");
      }
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: UpdateServiceInput) => api.services.update(service!.id, data),
    onSuccess: () => {
      onSuccess();
      onClose();
    },
    onError: (err) => {
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError("Failed to update service");
      }
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!name.trim()) {
      setError("Service name is required");
      return;
    }

    const durationNum = parseInt(duration, 10);
    if (isNaN(durationNum) || durationNum <= 0) {
      setError("Duration must be a positive number");
      return;
    }

    const priceNum = parseFloat(price);
    if (isNaN(priceNum) || priceNum < 0) {
      setError("Price must be a valid non-negative number");
      return;
    }

    const maxDogsNum = parseInt(maxDogs, 10);
    if (isNaN(maxDogsNum) || maxDogsNum <= 0) {
      setError("Max dogs must be a positive number");
      return;
    }

    // Parse optional numeric fields
    const bufferBeforeNum = bufferBefore ? parseInt(bufferBefore, 10) : null;
    const bufferAfterNum = bufferAfter ? parseInt(bufferAfter, 10) : null;
    const maxParticipantsNum = isGroupClass ? parseInt(maxParticipants, 10) : null;

    if (bufferBefore && (isNaN(bufferBeforeNum!) || bufferBeforeNum! < 0)) {
      setError("Buffer before must be a non-negative number");
      return;
    }
    if (bufferAfter && (isNaN(bufferAfterNum!) || bufferAfterNum! < 0)) {
      setError("Buffer after must be a non-negative number");
      return;
    }
    if (isGroupClass && (isNaN(maxParticipantsNum!) || maxParticipantsNum! < 1)) {
      setError("Max participants must be at least 1");
      return;
    }
    if (depositAmount && !depositType) {
      setError("Please select a deposit type");
      return;
    }

    // Build data object, only including fields that have values
    // Zod schemas use .optional() which means "can be omitted", not "can be null"
    const data: CreateServiceInput = {
      name: name.trim(),
      duration: durationNum,
      price: priceNum.toFixed(2),
      requiresDog,
      maxDogsPerAppointment: maxDogsNum,
      isProgram,
      isGroupClass,
      isPublic,
    };

    // Only include optional fields if they have values
    if (description.trim()) {
      data.description = description.trim();
    }
    if (color) {
      data.color = color;
    }
    if (category.trim()) {
      data.category = category.trim();
    }
    if (bufferBeforeNum !== null) {
      data.bufferBeforeMinutes = bufferBeforeNum;
    }
    if (bufferAfterNum !== null) {
      data.bufferAfterMinutes = bufferAfterNum;
    }
    if (depositAmount && depositType) {
      data.depositAmount = parseFloat(depositAmount).toFixed(2);
      data.depositType = depositType;
    }
    if (maxParticipantsNum !== null) {
      data.maxParticipants = maxParticipantsNum;
    }
    // Include calendar IDs - this is required for appointment availability
    if (selectedCalendarIds.length > 0) {
      data.calendarIds = selectedCalendarIds;
    }

    if (isEdit) {
      updateMutation.mutate(data);
    } else {
      createMutation.mutate(data);
    }
  };

  const isPending = createMutation.isPending || updateMutation.isPending;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto">
        <h2 className="text-xl font-semibold mb-4">
          {isEdit ? "Edit Service" : "Add New Service"}
        </h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="serviceName" className="block text-sm font-medium text-gray-700">
              Service name
            </label>
            <input
              id="serviceName"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-amber-500 focus:outline-none focus:ring-amber-500"
              required
            />
          </div>

          <div>
            <label htmlFor="description" className="block text-sm font-medium text-gray-700">
              Description
            </label>
            <textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-amber-500 focus:outline-none focus:ring-amber-500"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="duration" className="block text-sm font-medium text-gray-700">
                Duration (minutes)
              </label>
              <input
                id="duration"
                type="number"
                min="1"
                value={duration}
                onChange={(e) => setDuration(e.target.value)}
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-amber-500 focus:outline-none focus:ring-amber-500"
                required
              />
            </div>
            <div>
              <label htmlFor="price" className="block text-sm font-medium text-gray-700">
                Price ($)
              </label>
              <input
                id="price"
                type="number"
                min="0"
                step="0.01"
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-amber-500 focus:outline-none focus:ring-amber-500"
                required
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="category" className="block text-sm font-medium text-gray-700">
                Category
              </label>
              <input
                id="category"
                type="text"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                placeholder="e.g., Training, Grooming"
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-amber-500 focus:outline-none focus:ring-amber-500"
              />
            </div>
            <div>
              <label htmlFor="color" className="block text-sm font-medium text-gray-700">
                Color
              </label>
              <div className="mt-1 flex items-center gap-2">
                <div
                  className="w-8 h-8 rounded-md border border-gray-300"
                  style={{ backgroundColor: color }}
                />
                <select
                  id="color"
                  value={color}
                  onChange={(e) => setColor(e.target.value)}
                  className="flex-1 rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-amber-500 focus:outline-none focus:ring-amber-500"
                >
                  {SERVICE_COLORS.map((c) => (
                    <option key={c.value} value={c.value}>
                      {c.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="maxDogs" className="block text-sm font-medium text-gray-700">
                Max dogs per appointment
              </label>
              <input
                id="maxDogs"
                type="number"
                min="1"
                value={maxDogs}
                onChange={(e) => setMaxDogs(e.target.value)}
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-amber-500 focus:outline-none focus:ring-amber-500"
                required
              />
            </div>
            <div className="flex items-center pt-6">
              <input
                id="requiresDog"
                type="checkbox"
                checked={requiresDog}
                onChange={(e) => setRequiresDog(e.target.checked)}
                className="h-4 w-4 rounded border-gray-300 text-amber-600 focus:ring-amber-500"
              />
              <label htmlFor="requiresDog" className="ml-2 text-sm text-gray-700">
                Requires a dog
              </label>
            </div>
          </div>

          {/* Service Type Section */}
          <div className="border-t pt-4 mt-4">
            <h3 className="text-sm font-medium text-gray-900 mb-3">Service Type</h3>
            <div className="space-y-3">
              <div className="flex items-start">
                <input
                  id="isProgram"
                  type="checkbox"
                  checked={isProgram}
                  onChange={(e) => setIsProgram(e.target.checked)}
                  className="h-4 w-4 mt-0.5 rounded border-gray-300 text-amber-600 focus:ring-amber-500"
                />
                <div className="ml-2">
                  <label htmlFor="isProgram" className="text-sm text-gray-700 font-medium">
                    Program Service
                  </label>
                  <p className="text-xs text-gray-500">
                    Track dog progress through this service over multiple appointments
                  </p>
                </div>
              </div>
              <div className="flex items-start">
                <input
                  id="isGroupClass"
                  type="checkbox"
                  checked={isGroupClass}
                  onChange={(e) => setIsGroupClass(e.target.checked)}
                  className="h-4 w-4 mt-0.5 rounded border-gray-300 text-amber-600 focus:ring-amber-500"
                />
                <div className="ml-2">
                  <label htmlFor="isGroupClass" className="text-sm text-gray-700 font-medium">
                    Group Class
                  </label>
                  <p className="text-xs text-gray-500">
                    Multiple clients can attend at once
                  </p>
                </div>
              </div>
              {isGroupClass && (
                <div className="ml-6">
                  <label htmlFor="maxParticipants" className="block text-sm font-medium text-gray-700">
                    Max participants
                  </label>
                  <input
                    id="maxParticipants"
                    type="number"
                    min="1"
                    value={maxParticipants}
                    onChange={(e) => setMaxParticipants(e.target.value)}
                    className="mt-1 block w-32 rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-amber-500 focus:outline-none focus:ring-amber-500"
                  />
                </div>
              )}
              <div className="flex items-start">
                <input
                  id="isPublic"
                  type="checkbox"
                  checked={isPublic}
                  onChange={(e) => setIsPublic(e.target.checked)}
                  className="h-4 w-4 mt-0.5 rounded border-gray-300 text-amber-600 focus:ring-amber-500"
                />
                <div className="ml-2">
                  <label htmlFor="isPublic" className="text-sm text-gray-700 font-medium">
                    Public
                  </label>
                  <p className="text-xs text-gray-500">
                    Clients can book this service themselves
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Buffer Times Section */}
          <div className="border-t pt-4 mt-4">
            <h3 className="text-sm font-medium text-gray-900 mb-3">Buffer Times</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="bufferBefore" className="block text-sm font-medium text-gray-700">
                  Before (minutes)
                </label>
                <input
                  id="bufferBefore"
                  type="number"
                  min="0"
                  value={bufferBefore}
                  onChange={(e) => setBufferBefore(e.target.value)}
                  placeholder="0"
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-amber-500 focus:outline-none focus:ring-amber-500"
                />
              </div>
              <div>
                <label htmlFor="bufferAfter" className="block text-sm font-medium text-gray-700">
                  After (minutes)
                </label>
                <input
                  id="bufferAfter"
                  type="number"
                  min="0"
                  value={bufferAfter}
                  onChange={(e) => setBufferAfter(e.target.value)}
                  placeholder="0"
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-amber-500 focus:outline-none focus:ring-amber-500"
                />
              </div>
            </div>
          </div>

          {/* Deposit Section */}
          <div className="border-t pt-4 mt-4">
            <h3 className="text-sm font-medium text-gray-900 mb-3">Deposit</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="depositAmount" className="block text-sm font-medium text-gray-700">
                  Amount
                </label>
                <input
                  id="depositAmount"
                  type="number"
                  min="0"
                  step="0.01"
                  value={depositAmount}
                  onChange={(e) => setDepositAmount(e.target.value)}
                  placeholder="0.00"
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-amber-500 focus:outline-none focus:ring-amber-500"
                />
              </div>
              <div>
                <label htmlFor="depositType" className="block text-sm font-medium text-gray-700">
                  Type
                </label>
                <select
                  id="depositType"
                  value={depositType}
                  onChange={(e) => setDepositType(e.target.value as "fixed" | "percentage" | "")}
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-amber-500 focus:outline-none focus:ring-amber-500"
                >
                  <option value="">No deposit</option>
                  <option value="fixed">Fixed amount ($)</option>
                  <option value="percentage">Percentage (%)</option>
                </select>
              </div>
            </div>
          </div>

          {/* Calendar Assignment Section */}
          <div className="border-t pt-4 mt-4">
            <h3 className="text-sm font-medium text-gray-900 mb-2">Calendar Assignment</h3>
            <p className="text-xs text-gray-500 mb-3">
              Select which calendars can perform this service. Required for booking availability.
            </p>
            {calendars.length === 0 ? (
              <p className="text-sm text-amber-600">
                No calendars found. Create a calendar first to enable booking.
              </p>
            ) : (
              <div className="space-y-2 max-h-40 overflow-y-auto border rounded-md p-3">
                {calendars.map((calendar) => (
                  <label key={calendar.id} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectedCalendarIds.includes(calendar.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedCalendarIds([...selectedCalendarIds, calendar.id]);
                        } else {
                          setSelectedCalendarIds(selectedCalendarIds.filter(id => id !== calendar.id));
                        }
                      }}
                      className="h-4 w-4 rounded border-gray-300 text-amber-600 focus:ring-amber-500"
                    />
                    <div
                      className="w-3 h-3 rounded-full"
                      style={{ backgroundColor: calendar.color }}
                    />
                    <span className="text-sm text-gray-700">{calendar.name}</span>
                    <span className="text-xs text-gray-400">({calendar.calendarType})</span>
                  </label>
                ))}
              </div>
            )}
          </div>

          {error && (
            <div className="rounded-md bg-red-50 p-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          <div className="flex gap-3 justify-end pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isPending}
              className="px-4 py-2 text-sm font-medium text-white bg-amber-600 rounded-md hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isPending ? "Saving..." : isEdit ? "Save Changes" : "Create Service"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

interface DeleteConfirmModalProps {
  service: Service;
  onClose: () => void;
  onConfirm: () => void;
  isPending: boolean;
  error?: string;
}

function DeleteConfirmModal({ service, onClose, onConfirm, isPending, error }: DeleteConfirmModalProps) {
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-sm w-full p-6">
        <h2 className="text-xl font-semibold mb-4">Delete Service?</h2>
        <p className="text-gray-600 mb-4">
          Are you sure you want to delete &quot;{service.name}&quot;? This will deactivate the
          service and it will no longer be available for booking.
        </p>
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
            {error}
          </div>
        )}
        <div className="flex gap-3 justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={isPending}
            className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isPending ? "Deleting..." : "Delete"}
          </button>
        </div>
      </div>
    </div>
  );
}

function formatDuration(minutes: number): string {
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (mins === 0) return `${hours}h`;
  return `${hours}h ${mins}m`;
}

function ServiceBadge({ children, color }: { children: React.ReactNode; color: string }) {
  const colorClasses: Record<string, string> = {
    blue: "bg-blue-100 text-blue-700",
    purple: "bg-purple-100 text-purple-700",
    green: "bg-green-100 text-green-700",
    amber: "bg-amber-100 text-amber-700",
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${colorClasses[color] ?? colorClasses.blue}`}>
      {children}
    </span>
  );
}

function ServiceRow({
  service,
  onEdit,
  onDelete,
}: {
  service: Service;
  onEdit: (service: Service) => void;
  onDelete: (service: Service) => void;
}) {
  const hasDeposit = service.depositAmount && parseFloat(service.depositAmount) > 0;

  return (
    <tr className="hover:bg-gray-50">
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="flex items-center gap-3">
          <div
            className="w-3 h-3 rounded-full flex-shrink-0"
            style={{ backgroundColor: service.color ?? "#6b7280" }}
          />
          <div>
            <span className="font-medium text-gray-900">{service.name}</span>
            <div className="flex gap-1 mt-1">
              {service.isProgram && <ServiceBadge color="purple">Program</ServiceBadge>}
              {service.isGroupClass && <ServiceBadge color="blue">Group</ServiceBadge>}
              {hasDeposit && <ServiceBadge color="amber">Deposit</ServiceBadge>}
              {!service.isPublic && <ServiceBadge color="green">Private</ServiceBadge>}
            </div>
          </div>
        </div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
        {formatDuration(service.duration)}
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
        ${parseFloat(service.price).toFixed(2)}
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
        {service.category ?? "-"}
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm">
        {service.isActive ? (
          <span className="text-green-600">Active</span>
        ) : (
          <span className="text-gray-400">Inactive</span>
        )}
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-right">
        <button
          onClick={() => onEdit(service)}
          className="text-amber-600 hover:text-amber-700 mr-4"
        >
          Edit
        </button>
        <button onClick={() => onDelete(service)} className="text-red-600 hover:text-red-700">
          Delete
        </button>
      </td>
    </tr>
  );
}

export function ServicesPage() {
  const [page, setPage] = useState(0);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingService, setEditingService] = useState<Service | null>(null);
  const [deletingService, setDeletingService] = useState<Service | null>(null);
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ["services", { page }],
    queryFn: () => api.services.list({ page, limit: 20 }),
  });

  const [deleteError, setDeleteError] = useState("");

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.services.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["services"] });
      setDeletingService(null);
      setDeleteError("");
    },
    onError: (err) => {
      setDeleteError(err instanceof ApiError ? err.message : "Failed to delete service");
    },
  });

  const handleSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ["services"] });
  };

  if (error) {
    return (
      <div className="p-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-700">
            {error instanceof ApiError ? error.message : "Failed to load services"}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Services</h1>
        <button
          onClick={() => setShowCreateModal(true)}
          className="px-4 py-2 text-sm font-medium text-white bg-amber-600 rounded-md hover:bg-amber-700"
        >
          Add Service
        </button>
      </div>

      {isLoading ? (
        <div className="text-center py-12">
          <div className="animate-pulse text-gray-500">Loading services...</div>
        </div>
      ) : data?.data.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <p className="text-gray-500">No services yet. Add your first service to get started.</p>
        </div>
      ) : (
        <>
          <div className="bg-white shadow rounded-lg overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Duration
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Price
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Category
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {data?.data.map((service) => (
                  <ServiceRow
                    key={service.id}
                    service={service}
                    onEdit={setEditingService}
                    onDelete={setDeletingService}
                  />
                ))}
              </tbody>
            </table>
          </div>

          {data && data.pagination.totalPages > 1 && (
            <div className="mt-4 flex items-center justify-between">
              <p className="text-sm text-gray-700">
                Page {data.pagination.page + 1} of {data.pagination.totalPages} (
                {data.pagination.total} services)
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                  disabled={page === 0}
                  className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <button
                  onClick={() => setPage((p) => p + 1)}
                  disabled={page >= data.pagination.totalPages - 1}
                  className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {showCreateModal && (
        <ServiceForm onClose={() => setShowCreateModal(false)} onSuccess={handleSuccess} />
      )}

      {editingService && (
        <ServiceForm
          service={editingService}
          onClose={() => setEditingService(null)}
          onSuccess={handleSuccess}
        />
      )}

      {deletingService && (
        <DeleteConfirmModal
          service={deletingService}
          onClose={() => {
            setDeletingService(null);
            setDeleteError("");
          }}
          onConfirm={() => deleteMutation.mutate(deletingService.id)}
          isPending={deleteMutation.isPending}
          error={deleteError}
        />
      )}
    </div>
  );
}
